import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Calendar, FileText, ArrowRight, User, Eye } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Header } from '../Header';
import { Footer } from '../Footer';
import { blogAPI } from '../../services/api';
import { formatDateTime } from '../../lib/date';

export const BlogList = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const data = await blogAPI.getPosts();
      setPosts(data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Helmet>
        <title>Actualités AGIPLUS CI | BTP, Mécanique, Hydraulique & Location en Côte d'Ivoire</title>
        <meta
          name="description"
          content="Toutes les actualités d'AGIPLUS CI : réalisations BTP, génie civil, mécanique, hydraulique et location de matériel en Côte d'Ivoire."
        />
        <meta
          name="keywords"
          content="actualités BTP Côte d'Ivoire, AGIPLUS CI, génie civil Abidjan, hydraulique, location d'engins Abidjan"
        />
      </Helmet>
      <Header />

      <section className="py-24 bg-gray-50 mt-24">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              <span className="text-green-600">Actualités</span> & Newsletters
            </h1>
            <p className="text-lg text-gray-700">
              Restez informés de nos dernières actualités, innovations et réalisations
            </p>
          </div>

          {loading ? (
            <p className="text-center text-gray-500">Chargement...</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => (
                <Card key={post.id} data-testid={`blog-card-${post.slug}`} className="group hover:shadow-xl transition-all duration-300 bg-white overflow-hidden flex flex-col">
                  {post.image && (
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      {post.pdfUrl && (
                        <div className="absolute top-4 right-4 bg-orange-500 px-3 py-1 rounded-full">
                          <FileText className="h-4 w-4 text-white" />
                        </div>
                      )}
                    </div>
                  )}
                  <CardContent className="p-6 flex flex-col flex-1">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600 mb-3">
                      <span className="flex items-center gap-1.5" data-testid={`blog-date-${post.slug}`}>
                        <Calendar className="h-3.5 w-3.5 text-green-600" />
                        Publié le {formatDateTime(post.published_at)}
                      </span>
                      {post.author && (
                        <span className="flex items-center gap-1.5">
                          <User className="h-3.5 w-3.5 text-orange-500" />
                          {post.author}
                        </span>
                      )}
                      <span className="flex items-center gap-1.5">
                        <Eye className="h-3.5 w-3.5 text-gray-400" />
                        {post.views || 0} vue{(post.views || 0) > 1 ? 's' : ''}
                      </span>
                    </div>

                    <h2 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-green-600 transition-colors">
                      {post.title}
                    </h2>

                    <p className="text-gray-600 mb-4 line-clamp-3 flex-1">{post.excerpt}</p>

                    <Link to={`/actualites/${post.slug}`}>
                      <Button className="bg-green-600 hover:bg-green-700 text-white w-full" data-testid={`blog-read-${post.slug}`}>
                        Lire plus
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {!loading && posts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">Aucune actualité disponible pour le moment</p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};
